// vendor libs
import app from 'app/common/lib/app';
import context from 'app/common/lib/context';
import DomUtil from 'app/common/lib/DomUtil';
import isEmpty from 'lodash/isEmpty';
import LogToServer from 'app/common/lib/LogToServer';
import Router from 'app/common/lib/Router';
import timer from 'app/common/lib/timer';
import user from 'app/common/lib/User';

const REPORT_URL_FULL_PATH =
  window.location.protocol + '//' + window.location.host + LogToServer.BASE_URL_PATH;

let ReportMetricsHandlers;

const ReportMetricsOnLoadState = {
  onLoadFlag: false,
  loadTimeoutFlag: false,
  fired: false,
  loadTimeoutId: null,
  metricsSent: false,
  reportResourceTimingsIntervalId: null,
  reportPageLoadMetricsIntervalId: null,
  pureReactRouterPaths: [],

  /**
   * An internal function that checks state of the various events and fires
   * the report page load
   */
  _reportOnPageLoad() {
    if (ReportMetricsOnLoadState.onLoadFlag || ReportMetricsOnLoadState.loadTimeoutFlag) {
      if (!ReportMetricsOnLoadState.fired) {
        ReportMetricsOnLoadState.fired = true;
        ReportMetricsHandlers.reportPageLoadMetricsOnLoad();
      }
    }
  },
};

ReportMetricsHandlers = {
  /**
   * If we call _onLoadHandler right on onLoad window event
   * window.performance.timing['loadEventEnd'] will be 0. We have to wait until onLoad event will finish completely
   */
  _onLoadHandler() {
    if (window.performance.timing && window.performance.timing.loadEventEnd > 0) {
      ReportMetricsOnLoadState.onLoadFlag = true;
      ReportMetricsOnLoadState._reportOnPageLoad();
      if (ReportMetricsOnLoadState.loadTimeoutId !== null) {
        timer.clearTimeout(ReportMetricsOnLoadState.loadTimeoutId);
        ReportMetricsOnLoadState.loadTimeoutId = null;
      }
    } else {
      timer.setTimeout(this._onLoadHandler.bind(this), 1000);
    }
  },

  /**
   * For a full page load, given react-router resolved routes array, set the resolved React Router paths
   * patterns to send for validation to the Hive job downstream which processes the full page metrics
   */
  setPureReactRouterPaths(routes) {
    const paths = [];
    routes.forEach((route) => {
      if (route.path) {
        paths.push(route.path);
      }
    });
    ReportMetricsOnLoadState.pureReactRouterPaths = paths;
  },

  /**
   * Report page load metrics when the page has loaded or 120 seconds has passed
   * and the pageLoad event hasn't fired yet
   */
  reportPageLoadMetricsRegisterOnLoad() {
    // this fires immediately if the onLoad event has already occurred
    window.addEventListener('load', this._onLoadHandler.bind(this));

    ReportMetricsOnLoadState.loadTimeoutId = timer.setTimeout(() => {
      ReportMetricsOnLoadState.loadTimeoutFlag = true;
      ReportMetricsOnLoadState._reportOnPageLoad();
    }, 120000); // Just in case the page never loads, send metrics after 2 minutes

    window.addEventListener('beforeunload', () => {
      if (!ReportMetricsOnLoadState.metricsSent) {
        ReportMetricsHandlers.reportPageLoadMetrics();
      }

      if (ReportMetricsOnLoadState.loadTimeoutId !== null) {
        timer.clearTimeout(ReportMetricsOnLoadState.loadTimeoutId);
        ReportMetricsOnLoadState.loadTimeoutId = null;
      }
      if (ReportMetricsOnLoadState.reportResourceTimingsIntervalId !== null) {
        timer.clearInterval(ReportMetricsOnLoadState.reportResourceTimingsIntervalId);
        ReportMetricsOnLoadState.reportResourceTimingsIntervalId = null;
      }
    });
  },

  /**
   * Report page load metrics.
   */
  reportPageLoadMetricsOnLoad() {
    if (!context.initialPageContext.reportPageLoadTimings) {
      return;
    }
    ReportMetricsOnLoadState.reportPageLoadMetricsIntervalId = timer.setInterval(() => {
      ReportMetricsHandlers.reportPageLoadMetrics();
    }, 2000);
  },

  reportPageLoadMetrics() {
    try {
      const data = {};
      ReportMetricsHandlers.addFirstPinTiming(data);
      if (context.initialPageContext.reportBrowserResourceTimings) {
        ReportMetricsHandlers.addResourcePerfData(data);
        if (context.initialPageContext.reportPeriodicResourceTimings) {
          ReportMetricsHandlers.schedulePeriodicResourcePerfReport();
        }
      }
      ReportMetricsHandlers.addNavigationTypeData(data);
      ReportMetricsHandlers.addNavigationPerfData(data);
      ReportMetricsHandlers.addFirstPaintData(data);
      data.isPageLoad = true;
      ReportMetricsHandlers.logToServer(data);
      ReportMetricsOnLoadState.metricsSent = true;
      if (ReportMetricsOnLoadState.reportPageLoadMetricsIntervalId) {
        timer.clearTimeout(ReportMetricsOnLoadState.reportPageLoadMetricsIntervalId);
        ReportMetricsOnLoadState.reportPageLoadMetricsIntervalId = null;
      }
      DomUtil.destroyViewportBoundingBox();
    } catch (e) {
      if (ReportMetricsOnLoadState.reportPageLoadMetricsIntervalId) {
        timer.clearTimeout(ReportMetricsOnLoadState.reportPageLoadMetricsIntervalId);
        ReportMetricsOnLoadState.reportPageLoadMetricsIntervalId = null;
      }
      DomUtil.destroyViewportBoundingBox();
      // let the global exception handler handles the exception.
      throw e;
    }
  },

  /**
   * Add resouce timing for first pin image.
   */
  addFirstPinTiming(data) {
    if (!window.performance || !window.performance.getEntriesByType) {
      return;
    }
    const firstPin = window.performance
      .getEntriesByType('resource')
      .filter((res) => res.name.match(/com\/\d+x/))[0];

    if (firstPin) {
      data.firstPinTiming = {
        url: firstPin.name,
        start: Math.floor(firstPin.startTime),
        duration: Math.floor(firstPin.duration),
        responseEnd: Math.floor(firstPin.responseEnd),
      };
    }
  },

  /**
   * Add resource timings for all the resources from the previous call to this function.
   *
   * window.performance.getEntriesByType is quirky. There's a spec'd limit of 150 for the
   * entries buffer, and not all browsers implement the `clearResourceTimings` API to clear
   * the buffer and make room for new entries.
   */
  addResourcePerfData(data) {
    if (!window.performance || !window.performance.getEntriesByType) {
      return;
    }
    const resourcePerf = [];
    window.performance.getEntriesByType('resource').forEach((res) => {
      // Skip report urls.
      if (!res.name || res.name.indexOf(REPORT_URL_FULL_PATH) === 0) {
        return;
      }
      const resPerf = {};
      resPerf.url = res.name;
      Object.keys(res).forEach((key) => {
        // collect all non-zero metrics.
        if (typeof res[key] === 'number' && res[key] > 0) {
          resPerf[key] = Math.floor(res[key]);
        }
      });
      resourcePerf.push(resPerf);
    });
    if (resourcePerf.length > 0) {
      data.resourceTiming = resourcePerf;
    }
  },

  /*
   * Schedule a periodic report to send resource timings from the last report.
   * We do this only if we can clear the previous resource timings.
   */
  schedulePeriodicResourcePerfReport() {
    if (!window.performance || !window.performance.clearResourceTimings) {
      return;
    }

    ReportMetricsOnLoadState.reportResourceTimingsIntervalId = timer.setInterval(
      ReportMetricsHandlers.reportResourceMetrics,
      30000,
    ); // Send resource metrics every 30 seconds.
  },

  /*
   * Report resource timing metrics.
   */
  reportResourceMetrics() {
    if (!context.initialPageContext.reportPeriodicResourceTimings) {
      return;
    }
    const data = {};
    ReportMetricsHandlers.addResourcePerfData(data);
    if (!isEmpty(data)) {
      data.isPageLoad = false;
      ReportMetricsHandlers.logToServer(data);
    }
  },

  /*
   * Adds the type of navigation and # of redirects.
   */
  addNavigationTypeData(data) {
    if (!window.performance || !window.performance.navigation) {
      return;
    }
    const nav = window.performance.navigation;
    data.navigation = {
      type: nav.type,
      redirectCount: nav.redirectCount,
    };
  },

  /*
   * Adds navigation related performance data.
   */
  addNavigationPerfData(data) {
    if (!window.performance || !window.performance.timing) {
      return;
    }
    const perfTiming = window.performance.timing;
    const perfData = {};
    for (const key in perfTiming) {
      // collect all non-zero metrics.
      if (typeof perfTiming[key] === 'number' && perfTiming[key] > 0) {
        perfData[key] = perfTiming[key];
      }
    }
    data.performanceTiming = perfData;
  },

  /*
   * Adds firstPaint performance data.
   */
  addFirstPaintData(data) {
    if (!window.performance || !window.performance.timing) {
      return;
    }

    const paintEntries =
      (window.performance.getEntriesByType && window.performance.getEntriesByType('paint')) || [];
    const firstPaintEntry = paintEntries.find((entry) => entry.name === 'first-paint');

    if (firstPaintEntry) {
      // Default to native performance paint timing API.
      data.firstPaint = firstPaintEntry;
    } else if (window.chrome && window.chrome.loadTimes) {
      // If no native performance paint timing, fall back to deprecated
      // chrome.loadTimes().
      const loadTimes = window.chrome.loadTimes();
      data.firstPaint = (loadTimes.firstPaintTime - loadTimes.startLoadTime) * 1000;
    } else if (
      typeof window.performance.timing.msFirstPaint === 'number' &&
      window.performance.timing.navigationStart
    ) {
      // Use IE's first paint measurement. Guard against navigationStart = 0.
      const firstPaint = window.performance.timing.msFirstPaint;
      data.firstPaint = firstPaint - window.performance.timing.navigationStart;
    }
  },

  /**
   * Returns true if the main module is a react module, false otherwise.
   * We'll use this function until most of the modules are converted to react components.
   */
  isReactModule(moduleName) {
    const reactModuleNames = [
      'InterestFeedReactPage',
      'UnauthPinReactPage',
      'UnauthSamsungCameraPinPage',
      'UnauthBoardReactPage',
      'UnauthHomeReactPage',
      'UnauthLoginReactWrapper',
      'OpenInterestFeedReactPage',
      'UnauthOpenPinReactPage',
      'UnauthOpenClosedPinReactPage',
      'UnauthOpenBoardReactPage',
      'UnauthOpenClosedBoardReactPage',
    ];
    return reactModuleNames.includes(moduleName);
  },

  /*
   * If the passed in data is not empty, sends it to the server along with some
   * request/browser/module information.
   */
  logToServer(data) {
    if (isEmpty(data)) {
      return false;
    }

    const listOfData = Array.isArray(data) ? data : [data];
    const routeMatch = Router.instance.resolvePathToRoutePattern(window.location.pathname);
    const finalOutput = listOfData.map((d) => {
      d.pinterestRID = context.initialPageContext.pinterestRID;
      if (routeMatch && routeMatch.length > 0) {
        d.routePattern = routeMatch[0].rawPathPattern;
      } else if (d.isPageLoad && ReportMetricsOnLoadState.pureReactRouterPaths) {
        d.pureReactRoutePatterns = ReportMetricsOnLoadState.pureReactRouterPaths;
      }

      if (user.attributes.country) {
        d.country = user.attributes.country;
      }

      d.userAgent = window.navigator.userAgent;
      d.browser = context.instance.browser_name;
      d.isMobile = context.instance.is_mobile_agent;
      d.referrer = document.referrer;

      d.pathname = window.location.pathname;
      // TODO (jchuang) full-page react rendered pages don't have the concept
      // of a main module name. Need to modify the report_metric/statsd.py code
      // to not use moduleName and use route_pattern(?)
      if (app.instance) {
        d.moduleName = app.instance.debugGetCurrentMainModuleName();
      }
      if (this.isReactModule(d.moduleName)) {
        d.isReact = true;
      }
      LogToServer.logPerfDataToServer(
        LogToServer.SERVER_LOG_TYPE.PAGE_LOAD_METRIC,
        LogToServer.HTTP_METHOD.POST,
        d,
      );
      return d;
    });
    // So we can assert on it in tests
    return finalOutput;
  },
};

// Eslint complains about mutable `let` variables being exported so reassigning here.
const ReportMetrics = ReportMetricsHandlers;

export default ReportMetrics;
